/**
 * V77.1 — MakeoverExpertCard
 *
 * Reusable expert profile card for Ishpreet Kaur — used on the master, country
 * and category landing pages under /online-makeover-classes*. Also used in
 * the homepage teaser for cohesive branding.
 *
 * Contact links are admin-editable via /admin/settings (see PUBLIC_SETTINGS_FIELDS
 * in backend/server.py). Falls back to MAKEOVER_EXPERT.defaults if unset.
 *
 * Image is served from /public/makeover/ with responsive srcset (webp 400/640/960 + avif).
 * Uses loading="lazy" + decoding="async" everywhere except when `priority` is set.
 */
import { MessageCircle, Phone, Instagram, MapPin, ExternalLink, ShieldCheck } from "lucide-react";
import { MAKEOVER_EXPERT, makeoverWhatsAppUrl } from "../data/makeoverData";
import { useSettings } from "../context/SettingsContext";

export default function MakeoverExpertCard({ variant = "full", priority = false, testidSuffix = "" }) {
    const { settings } = useSettings();
    const s = settings || {};
    const whatsapp = (s.makeover_expert_whatsapp || MAKEOVER_EXPERT.defaults.whatsapp || "").replace(/[^0-9]/g, "");
    const phone = s.makeover_expert_phone || MAKEOVER_EXPERT.defaults.phone;
    const instagramUrl = s.makeover_expert_instagram_url || MAKEOVER_EXPERT.defaults.instagramUrl;
    const googleMapsUrl = s.makeover_expert_google_maps_url || MAKEOVER_EXPERT.defaults.googleMapsUrl;
    const waHref = makeoverWhatsAppUrl(whatsapp);
    const tid = testidSuffix ? `-${testidSuffix}` : "";

    if (variant === "compact") {
        return (
            <div data-testid={`makeover-expert-card${tid}`} className="flex items-center gap-4 rounded-2xl border border-rose-100 bg-white p-4">
                <img
                    src={MAKEOVER_EXPERT.image}
                    srcSet={MAKEOVER_EXPERT.imageSrcSet}
                    sizes="80px"
                    alt={MAKEOVER_EXPERT.imageAlt}
                    loading={priority ? "eager" : "lazy"}
                    decoding="async"
                    width={80} height={120}
                    className="w-20 h-24 sm:w-24 sm:h-28 object-cover rounded-xl shadow-sm shrink-0"
                />
                <div className="min-w-0">
                    <div className="font-heading font-bold text-slate-900 truncate">{MAKEOVER_EXPERT.name}</div>
                    <div className="text-[11px] text-rose-600 font-semibold uppercase tracking-wider">{MAKEOVER_EXPERT.title}</div>
                    <div className="text-xs text-slate-500 mt-0.5 truncate">{MAKEOVER_EXPERT.servingLine}</div>
                </div>
            </div>
        );
    }

    return (
        <section
            id="meet-your-makeover-expert"
            data-testid={`makeover-expert-card${tid}`}
            className="py-14 bg-gradient-to-br from-rose-50 via-white to-amber-50 border-y border-rose-100"
        >
            <div className="max-w-6xl mx-auto px-6 lg:px-8">
                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-rose-600 mb-3">Meet Your Expert</div>
                <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-8 max-w-3xl">
                    About the Expert
                </h2>

                <div className="grid lg:grid-cols-5 gap-8 items-center">
                    {/* Photo — modern rounded corners + subtle shadow */}
                    <div className="lg:col-span-2 order-1 lg:order-none">
                        <div className="relative max-w-sm mx-auto lg:mx-0">
                            <div className="absolute -inset-3 rounded-[2rem] bg-gradient-to-br from-rose-200 via-amber-100 to-transparent blur-2xl opacity-70" aria-hidden />
                            <img
                                src={MAKEOVER_EXPERT.image}
                                srcSet={MAKEOVER_EXPERT.imageSrcSet}
                                sizes={MAKEOVER_EXPERT.imageSizes}
                                alt={MAKEOVER_EXPERT.imageAlt}
                                loading={priority ? "eager" : "lazy"}
                                decoding="async"
                                width={640} height={960}
                                data-testid="makeover-expert-image"
                                className="relative w-full aspect-[2/3] object-cover rounded-3xl shadow-2xl shadow-rose-200/60 ring-1 ring-rose-100"
                            />
                            <div className="absolute -bottom-4 -right-4 hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-white rounded-full shadow-lg border border-slate-200 text-[11px] font-semibold text-slate-700">
                                <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" /> Verified Expert
                            </div>
                        </div>
                    </div>

                    {/* Details */}
                    <div className="lg:col-span-3">
                        <h3 className="font-heading text-4xl sm:text-5xl font-bold text-slate-900 mb-1 tracking-tight">{MAKEOVER_EXPERT.name}</h3>
                        <div className="text-rose-600 font-semibold text-sm sm:text-base uppercase tracking-wide mb-2">{MAKEOVER_EXPERT.title}</div>
                        <div className="text-slate-600 text-sm sm:text-base mb-5">{MAKEOVER_EXPERT.subtitle}</div>

                        <div className="mb-6">
                            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-slate-500 mb-2">Serving</div>
                            <div className="flex flex-wrap gap-2">
                                {MAKEOVER_EXPERT.servingRegions.map((r) => (
                                    <span key={r} className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-white border border-rose-200 text-rose-700">
                                        <MapPin className="w-3 h-3" /> {r}
                                    </span>
                                ))}
                            </div>
                        </div>

                        <div className="grid sm:grid-cols-2 gap-3 mb-5">
                            <a
                                href={waHref}
                                target="_blank" rel="noopener noreferrer"
                                data-smart-contact-skip="true"
                                data-testid="makeover-expert-whatsapp"
                                className="inline-flex items-center gap-2 justify-center bg-[#22C55E] hover:bg-[#16A34A] text-white font-bold px-4 py-3 rounded-xl text-sm shadow-md shadow-emerald-200/60"
                            >
                                <MessageCircle className="w-4 h-4" /> WhatsApp Makeover Team
                            </a>
                            <a
                                href={`tel:${(phone || "").replace(/\s+/g, "")}`}
                                data-testid="makeover-expert-call"
                                className="inline-flex items-center gap-2 justify-center bg-slate-900 hover:bg-slate-700 text-white font-bold px-4 py-3 rounded-xl text-sm"
                            >
                                <Phone className="w-4 h-4" /> Call Makeover Team
                            </a>
                        </div>

                        <div className="flex flex-wrap gap-3">
                            {instagramUrl && (
                                <a
                                    href={instagramUrl}
                                    target="_blank" rel="noopener noreferrer"
                                    data-testid="makeover-expert-instagram"
                                    className="inline-flex items-center gap-2 bg-white border border-slate-200 hover:border-rose-300 text-slate-800 font-semibold px-4 py-2 rounded-lg text-sm"
                                >
                                    <Instagram className="w-4 h-4 text-rose-500" /> Follow on Instagram
                                </a>
                            )}
                            {googleMapsUrl && (
                                <a
                                    href={googleMapsUrl}
                                    target="_blank" rel="noopener noreferrer"
                                    data-testid="makeover-expert-google"
                                    className="inline-flex items-center gap-2 bg-white border border-slate-200 hover:border-blue-300 text-slate-800 font-semibold px-4 py-2 rounded-lg text-sm"
                                >
                                    <MapPin className="w-4 h-4 text-[#4285F4]" /> View on Google
                                    <ExternalLink className="w-3 h-3 opacity-60" />
                                </a>
                            )}
                        </div>

                        <p className="mt-6 text-xs text-slate-500 max-w-md">
                            Every 1-on-1 live session is scheduled around YOUR timezone. Auto-recorded, privately shared with you for revision. Session cadence + package details discussed on the enquiry call.
                        </p>
                    </div>
                </div>

                {/* V79 — Authentic biography (owner-provided) */}
                <div className="mt-12 pt-10 border-t border-rose-100" data-testid="makeover-expert-bio">
                    <div className="grid lg:grid-cols-5 gap-8">
                        <div className="lg:col-span-2">
                            <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-rose-600 mb-2">About</div>
                            <h3 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 mb-3">
                                About {MAKEOVER_EXPERT.name}
                            </h3>
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-rose-200 text-[11px] font-semibold text-rose-700 mb-4">
                                <ShieldCheck className="w-3 h-3" /> {MAKEOVER_EXPERT.experienceYears} · Professional Makeup Artist & Educator
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                                {MAKEOVER_EXPERT.specialties.map((s) => (
                                    <span key={s} className="inline-flex items-center px-2.5 py-1 rounded-full bg-rose-50 border border-rose-100 text-[10px] font-semibold text-rose-700 uppercase tracking-wider">
                                        {s}
                                    </span>
                                ))}
                            </div>
                        </div>
                        <div className="lg:col-span-3 space-y-4">
                            {MAKEOVER_EXPERT.bioParagraphs.map((p, i) => (
                                <p key={i} className="text-slate-700 text-sm sm:text-base leading-relaxed">{p}</p>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
